import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { Icon } from '../components/icons';
import { Badge, EmptyState, ErrorState, Field, LoadingRows, Modal, PageHeader, ToneDot } from '../components/ui';
import { useToast } from '../context/ToastContext';
import { api } from '../lib/api';
import { errorMessage } from '../lib/errors';
import { formatDate, formatDateTime, formatPhone } from '../lib/format';
import { MOCK_MODE } from '../lib/api';
import { qk, useContact, useInbox, useTickets } from '../lib/queries';

/** S5 — Kişi 360: kimlik, opt-in, siparişler, ticket geçmişi, notlar. */
export default function Contact360Page() {
  const { id = '' } = useParams<{ id: string }>();
  const contact = useContact(id);
  const inbox = useInbox({ filter: 'all', search: '' });
  const tickets = useTickets('all');
  const toast = useToast();
  const queryClient = useQueryClient();

  const [notes, setNotes] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<'opt-out' | 'block' | null>(null);

  const saveNotes = useMutation({
    mutationFn: (value: string) => api.contactNotes(id, value),
    onSuccess: () => {
      toast.push('Not kaydedildi.', 'ok');
      setNotes(null);
      void queryClient.invalidateQueries({ queryKey: qk.contact(id) });
    },
    onError: (error) => toast.push(errorMessage(error), 'error')
  });

  const optOut = useMutation({
    mutationFn: () => api.contactOptOut(id),
    onSuccess: () => {
      toast.push('Opt-in kaldırıldı; pazarlama mesajı gönderilemez.', 'ok');
      setConfirm(null);
      void queryClient.invalidateQueries({ queryKey: qk.contact(id) });
    },
    onError: (error) => toast.push(errorMessage(error), 'error')
  });

  const block = useMutation({
    mutationFn: () => api.contactBlock(id),
    onSuccess: () => {
      toast.push('Kişi engellendi.', 'ok');
      setConfirm(null);
      void queryClient.invalidateQueries({ queryKey: qk.contact(id) });
    },
    onError: (error) => toast.push(errorMessage(error), 'error')
  });

  if (contact.isError) return <ErrorState message={errorMessage(contact.error)} onRetry={() => void contact.refetch()} />;
  if (contact.isLoading) return <LoadingRows rows={8} columns={3} />;
  if (!contact.data) return <EmptyState title="Kişi bulunamadı" hint="Numara kayıtlı değil." />;

  const data = contact.data;
  const conversation = inbox.data?.items.find((item) => item.phone === data.phone);
  const contactTickets = (tickets.data?.items ?? []).filter((ticket) => ticket.phone === data.phone);

  return (
    <div>
      <PageHeader
        title={data.name || formatPhone(data.phone)}
        subtitle={<span className="mono">{formatPhone(data.phone)}</span>}
        actions={
          <>
            {conversation ? (
              <Link to={'/konusma/' + conversation.id} className="btn btn-primary btn-sm">
                <Icon name="send" size={14} />
                Konuşmayı aç
              </Link>
            ) : null}
            <Link to="/inbox" className="btn btn-sm">
              <Icon name="inbox" size={14} />
              Gelen kutusu
            </Link>
          </>
        }
      />

      <div className="grid grid-cols-1 gap-4 p-6 min-[1200px]:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-4">
          <section className="panel">
            <div className="panel-head">
              <h2 className="text-[13px]">Siparişler</h2>
              <span className="mono text-[11px] text-muted">{data.orders.length} kayıt</span>
            </div>
            {data.orders.length === 0 ? (
              <EmptyState
                icon="file"
                title={MOCK_MODE ? 'Sipariş yok' : 'Sipariş verisi henüz bağlı değil'}
                hint={
                  MOCK_MODE
                    ? 'Bu numaraya bağlı fizipazar/AnkaraQR kaydı bulunamadı.'
                    : 'Fizipazar ve AnkaraQR adaptörleri (F8) bağlandığında siparişler burada listelenecek.'
                }
              />
            ) : (
              <table className="table">
                <caption className="sr-only">Kişinin siparişleri</caption>
                <thead>
                  <tr>
                    <th scope="col">Sipariş</th>
                    <th scope="col">Ürün</th>
                    <th scope="col">Tutar</th>
                    <th scope="col">Durum</th>
                    <th scope="col">Tarih</th>
                  </tr>
                </thead>
                <tbody>
                  {data.orders.map((order) => (
                    <tr key={order.id}>
                      <td className="mono text-[12px]">{order.id}</td>
                      <td className="max-w-[280px] truncate">{order.title}</td>
                      <td className="mono text-[12px]">{order.amount}</td>
                      <td>
                        <Badge>{order.status}</Badge>
                      </td>
                      <td className="mono text-[12px] text-muted">{formatDate(order.at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="panel">
            <div className="panel-head">
              <h2 className="text-[13px]">Ticket geçmişi</h2>
              <span className="mono text-[11px] text-muted">{contactTickets.length} kayıt</span>
            </div>
            {contactTickets.length === 0 ? (
              <EmptyState
                icon="ticket"
                title={MOCK_MODE ? 'Ticket yok' : 'Ticket uçları henüz yok'}
                hint={
                  MOCK_MODE
                    ? 'Bu kişi için açılmış destek talebi bulunmuyor.'
                    : 'Ticket listesi ucu yayınlandığında bu kişinin talepleri burada görünecek.'
                }
              />
            ) : (
              <ul>
                {contactTickets.map((ticket) => (
                  <li key={ticket.id} className="border-b border-line last:border-0">
                    <Link to={'/tickets/' + ticket.id} className="flex items-center gap-3 px-4 py-2 hover:bg-paper">
                      <span className="mono text-[11px] text-muted">{ticket.no}</span>
                      <span className="min-w-0 flex-1 truncate text-[13px]">{ticket.subject}</span>
                      <Badge tone={ticket.status === 'resolved' ? 'ok' : ticket.status === 'pending' ? 'warn' : 'neutral'}>
                        {ticket.status === 'resolved' ? 'çözüldü' : ticket.status === 'pending' ? 'bekliyor' : 'açık'}
                      </Badge>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="panel">
            <div className="panel-head">
              <h2 className="text-[13px]">Notlar</h2>
              <span className="text-[11px] text-muted">Yalnız ekip görür</span>
            </div>
            <div className="panel-body space-y-2">
              <Field
                label="İç not"
                htmlFor="contact-notes"
                hint={MOCK_MODE ? undefined : 'Not güncelleme ucu henüz yok; bu alan salt-okur.'}
              >
                <textarea
                  id="contact-notes"
                  className="textarea min-h-[96px]"
                  value={notes === null ? data.notes : notes}
                  onChange={(event) => setNotes(event.target.value)}
                  placeholder={MOCK_MODE ? 'Bu kişiyle ilgili ekip notu…' : data.notes || 'Not yok'}
                  readOnly={!MOCK_MODE}
                />
              </Field>
              {MOCK_MODE ? (
                <div className="flex justify-end gap-2">
                  <button type="button" className="btn btn-sm" onClick={() => setNotes(null)} disabled={notes === null}>
                    Vazgeç
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={notes === null || saveNotes.isPending}
                    onClick={() => notes !== null && saveNotes.mutate(notes)}
                  >
                    Notu kaydet
                  </button>
                </div>
              ) : null}
            </div>
          </section>
        </div>

        <aside className="space-y-4">
          <section className="panel">
            <div className="panel-head">
              <h2 className="text-[13px]">Kimlik</h2>
            </div>
            <dl className="divide-y divide-line text-[12px]">
              <div className="flex items-center justify-between px-4 py-2">
                <dt className="text-muted">Telefon</dt>
                <dd className="mono">{formatPhone(data.phone)}</dd>
              </div>
              <div className="flex items-center justify-between px-4 py-2">
                <dt className="text-muted">Opt-in</dt>
                <dd className="flex items-center gap-1">
                  <ToneDot tone={data.optIn ? 'ok' : 'danger'} />
                  {data.optIn ? 'var' : 'yok'}
                </dd>
              </div>
              <div className="flex items-center justify-between px-4 py-2">
                <dt className="text-muted">Opt-in tarihi</dt>
                <dd className="mono">{data.optInAt ? formatDate(data.optInAt) : '—'}</dd>
              </div>
              <div className="flex items-center justify-between px-4 py-2">
                <dt className="text-muted">Kayıt</dt>
                <dd className="mono">{formatDate(data.createdAt)}</dd>
              </div>
              <div className="flex items-center justify-between px-4 py-2">
                <dt className="text-muted">Son görülme</dt>
                <dd className="mono">{formatDateTime(data.lastSeenAt)}</dd>
              </div>
            </dl>
            <div className="flex flex-wrap gap-1 border-t border-line px-4 py-2">
              {data.labels.length === 0 ? <span className="text-[12px] text-muted">Etiket yok</span> : data.labels.map((label) => <Badge key={label}>{label}</Badge>)}
            </div>
          </section>

          <section className="panel">
            <div className="panel-head">
              <h2 className="text-[13px]">Aksiyonlar</h2>
            </div>
            <div className="panel-body space-y-2">
              <button
                type="button"
                className="btn w-full justify-start"
                disabled={!data.optIn}
                onClick={() => setConfirm('opt-out')}
              >
                <Icon name="lock" size={14} />
                Opt-in'i kaldır
              </button>
              <button
                type="button"
                className="btn btn-danger w-full justify-start"
                disabled={data.blocked}
                onClick={() => setConfirm('block')}
              >
                <Icon name="warning" size={14} />
                Kişiyi engelle
              </button>
              <p className="text-[11px] leading-4 text-muted">
                Engelleme ve opt-out audit kaydına yazılır; pazarlama mesajları durdurulur.
              </p>
            </div>
          </section>
        </aside>
      </div>

      <Modal
        open={confirm !== null}
        title={confirm === 'block' ? 'Kişiyi engelle' : "Opt-in'i kaldır"}
        onClose={() => setConfirm(null)}
        footer={
          <>
            <button type="button" className="btn btn-sm" onClick={() => setConfirm(null)}>
              Vazgeç
            </button>
            <button
              type="button"
              className={confirm === 'block' ? 'btn btn-danger btn-sm' : 'btn btn-primary btn-sm'}
              disabled={optOut.isPending || block.isPending}
              onClick={() => (confirm === 'block' ? block.mutate() : optOut.mutate())}
            >
              Onayla
            </button>
          </>
        }
      >
        <p className="text-[13px]">
          {confirm === 'block'
            ? 'Bu numaradan gelen mesajlar kaydedilir ancak yanıt gönderilmez. Devam edilsin mi?'
            : 'Pazarlama amaçlı mesaj izni kaldırılacak. Hizmet mesajları etkilenmez.'}
        </p>
      </Modal>
    </div>
  );
}
